/**
 * Board meeting detail — full lifecycle controls.
 *
 * Route: board/:meetingId  (reads :meetingId via useParams)
 *
 * STAFF / board-INTERNAL screen. Combines admin + board member views (the
 * same admin is also a board member, typically). Sections:
 *   - Status header + lifecycle controls (Start / End / Publish)
 *   - Attendance list (board members + present/unavailable status)
 *   - Voting items (add + cast votes + declare conflict + display results)
 *   - Minutes editor
 *   - Recording URL submission
 *   - Results email draft review (when status === 'ended')
 *
 * Ported from src/views/admin/BoardGovernance/MeetingDetail.jsx. Legacy
 * used react-router v5 (useHistory); admin.nurev.io is react-router v7
 * (useParams + useNavigate). Purple accent → amber. BackButton returns
 * to the list (board).
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Play,
  Square,
  Send,
  Plus,
  Check,
  X,
  Edit2,
  Video,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import {
  useBoardMeeting,
  useStartMeeting,
  useEndMeeting,
  usePublishResults,
  useUpdateMinutes,
  useSubmitRecording,
  useAddVotingItem,
  useMarkAttendance,
  useCastVote,
  useDeclareConflict
} from '@/hooks/useBoard';
import {
  BoardMeeting,
  MeetingAttendance,
  MeetingStatus,
  PopulatedShareholderRef,
  Vote,
  VotingItem,
  VotingItemType
} from '@/api/board';
import { Card, SectionLabel, BackButton } from '@/components/ui/atoms';
import { ResultsDraftEditor } from './ResultsDraftEditor';

const fmtDateTime = (d?: string | null): string => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

const STATUS_LABELS: Record<MeetingStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  ended: 'Voting Closed',
  concluded: 'Concluded',
  cancelled: 'Cancelled'
};

export function MeetingDetailScreen() {
  const { meetingId = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useBoardMeeting(meetingId);
  const startMeeting = useStartMeeting(meetingId);
  const endMeeting = useEndMeeting(meetingId);
  const publishResults = usePublishResults(meetingId);
  const updateMinutes = useUpdateMinutes(meetingId);
  const submitRecording = useSubmitRecording(meetingId);

  const [addingItem, setAddingItem] = useState(false);
  const [minutesDraft, setMinutesDraft] = useState<string | null>(null);
  const [recordingDraft, setRecordingDraft] = useState('');
  const [showResultsDraft, setShowResultsDraft] = useState(false);

  if (isLoading) {
    return <div className="text-sm text-nurev-muted">Loading meeting…</div>;
  }

  const meeting = data?.meeting;
  const items = data?.items ?? [];
  const attendance = data?.attendance ?? [];
  const votes = data?.votes ?? [];

  if (!meeting) {
    return (
      <div className="space-y-4">
        <BackButton onClick={() => navigate('/board')} label="All meetings" />
        <div className="text-sm text-nurev-muted">Meeting not found</div>
      </div>
    );
  }

  const status = meeting.status;
  const isScheduled = status === 'scheduled';
  const isInProgress = status === 'in_progress';
  const isEnded = status === 'ended';
  const isConcluded = status === 'concluded';

  const onStart = async () => {
    if (!confirm(`Start "${meeting.name}"? Voting will open.`)) return;
    await startMeeting.mutateAsync();
  };
  const onEnd = async () => {
    if (
      !confirm(
        'End the meeting? Voting will close and the results email draft will be generated.'
      )
    )
      return;
    await endMeeting.mutateAsync();
  };
  const onPublish = async () => {
    if (
      !confirm(
        'Publish results? An email will be sent to ALL shareholders and the meeting will be locked. This cannot be undone.'
      )
    )
      return;
    await publishResults.mutateAsync();
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      {/* Back */}
      <BackButton onClick={() => navigate('/board')} label="All meetings" />

      {/* Header */}
      <Card className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-nurev-muted">
              {meeting.meetingType} · {STATUS_LABELS[status]}
            </div>
            <h1 className="mt-1 text-2xl font-semibold text-nurev-text">
              {meeting.name}
            </h1>
            <div className="mt-1 text-sm text-nurev-muted">
              Scheduled: {fmtDateTime(meeting.scheduledAt)} PT
            </div>
            {meeting.description && (
              <div className="mt-2 max-w-prose text-sm text-nurev-muted">
                {meeting.description}
              </div>
            )}
          </div>
          {/* Lifecycle controls */}
          <div className="flex flex-col items-end gap-2">
            {isScheduled && (
              <button
                type="button"
                onClick={onStart}
                disabled={startMeeting.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-300 ring-1 ring-emerald-500/30 transition hover:bg-emerald-500/30 disabled:opacity-50"
              >
                <Play className="h-3.5 w-3.5" /> Start meeting
              </button>
            )}
            {isInProgress && (
              <button
                type="button"
                onClick={onEnd}
                disabled={endMeeting.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500/20 px-4 py-2 text-sm font-semibold text-amber-300 ring-1 ring-amber-500/30 transition hover:bg-amber-500/30 disabled:opacity-50"
              >
                <Square className="h-3.5 w-3.5" /> End meeting
              </button>
            )}
            {isEnded && (
              <>
                <button
                  type="button"
                  onClick={() => setShowResultsDraft(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-nurev-bg transition hover:bg-amber-400"
                >
                  <Edit2 className="h-3.5 w-3.5" /> Review results email
                </button>
                <button
                  type="button"
                  onClick={onPublish}
                  disabled={publishResults.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-300 ring-1 ring-emerald-500/30 transition hover:bg-emerald-500/30 disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" /> Publish to shareholders
                </button>
              </>
            )}
            {isConcluded && (
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-500/30">
                <CheckCircle className="h-3 w-3" /> Published{' '}
                {fmtDateTime(meeting.concludedAt)}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Attendance */}
      <AttendanceSection
        meetingId={meetingId}
        meeting={meeting}
        attendance={attendance}
      />

      {/* Voting items */}
      <VotingItemsSection
        meetingId={meetingId}
        meeting={meeting}
        items={items}
        votes={votes}
        addingItem={addingItem}
        setAddingItem={setAddingItem}
      />

      {/* Minutes */}
      <Card className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-nurev-text">Minutes</div>
          {meeting.minutesUpdatedAt && (
            <div className="text-[11px] text-nurev-muted">
              Updated {fmtDateTime(meeting.minutesUpdatedAt)}
            </div>
          )}
        </div>
        {!isConcluded && (
          <>
            <textarea
              value={minutesDraft !== null ? minutesDraft : meeting.minutes || ''}
              onChange={e => setMinutesDraft(e.target.value)}
              rows={8}
              placeholder="Discussion summary, decisions, attendees noted absent…"
              className="w-full resize-y rounded-lg border border-nurev-border bg-white/[0.04] px-3.5 py-2.5 text-sm text-nurev-text outline-none focus:border-amber-500/50"
            />
            {minutesDraft !== null && minutesDraft !== meeting.minutes && (
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setMinutesDraft(null)}
                  className="rounded-lg border border-nurev-border bg-white/[0.05] px-3 py-1.5 text-xs text-nurev-text"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await updateMinutes.mutateAsync(minutesDraft);
                    setMinutesDraft(null);
                  }}
                  disabled={updateMinutes.isPending}
                  className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-nurev-bg transition hover:bg-amber-400 disabled:opacity-50"
                >
                  {updateMinutes.isPending ? 'Saving…' : 'Save minutes'}
                </button>
              </div>
            )}
          </>
        )}
        {isConcluded && (
          <div
            className="whitespace-pre-wrap text-sm text-nurev-text"
            style={{ minHeight: 40 }}
          >
            {meeting.minutes || '(No minutes recorded.)'}
          </div>
        )}
      </Card>

      {/* Recording URL */}
      {!isScheduled && !isConcluded && (
        <Card className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-nurev-text">
            <Video className="h-3.5 w-3.5" /> Meeting recording
          </div>
          {meeting.recordingUrl ? (
            <div className="flex items-center justify-between gap-3 text-sm">
              <a
                href={meeting.recordingUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all text-amber-300 hover:text-amber-200"
              >
                {meeting.recordingUrl}
              </a>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={recordingDraft}
                onChange={e => setRecordingDraft(e.target.value)}
                placeholder="https://us02web.zoom.us/rec/share/…"
                className="flex-1 rounded-lg border border-nurev-border bg-white/[0.04] px-3.5 py-2.5 text-sm text-nurev-text outline-none focus:border-amber-500/50"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!/^https?:\/\//.test(recordingDraft)) {
                    alert('Recording URL must start with http:// or https://');
                    return;
                  }
                  await submitRecording.mutateAsync(recordingDraft);
                  setRecordingDraft('');
                }}
                disabled={submitRecording.isPending || !recordingDraft.trim()}
                className="rounded-lg bg-amber-500 px-4 py-2 text-xs font-semibold text-nurev-bg transition hover:bg-amber-400 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Results email draft modal */}
      {showResultsDraft && (
        <ResultsDraftEditor
          meetingId={meetingId}
          onClose={() => setShowResultsDraft(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Attendance section
// ─────────────────────────────────────────────────────────

function AttendanceSection({
  meetingId,
  meeting,
  attendance
}: {
  meetingId: string;
  meeting: BoardMeeting;
  attendance: MeetingAttendance[];
}) {
  const markAttendance = useMarkAttendance(meetingId);
  const isInProgress = meeting.status === 'in_progress';
  const isScheduled = meeting.status === 'scheduled';

  const present = attendance.filter(a => a.status === 'present');

  return (
    <Card className="space-y-3">
      <div className="text-sm font-semibold text-nurev-text">
        Attendance · {present.length} present
      </div>

      {isInProgress && (
        <div className="flex items-center gap-2 border-b border-nurev-border pb-3">
          <span className="text-xs text-nurev-muted">Your status:</span>
          <button
            type="button"
            onClick={() => markAttendance.mutateAsync('present')}
            disabled={markAttendance.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 disabled:opacity-50"
          >
            <Check className="h-3 w-3" /> I&apos;m here
          </button>
          {!isScheduled && (
            <button
              type="button"
              onClick={() => markAttendance.mutateAsync('unavailable')}
              disabled={markAttendance.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/[0.06] px-3 py-1.5 text-xs font-semibold text-nurev-muted disabled:opacity-50"
            >
              Unavailable
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
        {attendance.map(a => {
          const sh = a.shareholderId;
          if (!sh || typeof sh !== 'object') return null;
          const shObj = sh as PopulatedShareholderRef;
          const labelCls =
            a.status === 'present'
              ? 'text-emerald-300 bg-emerald-500/15'
              : a.status === 'unavailable'
                ? 'text-red-300 bg-red-500/15'
                : 'text-nurev-muted bg-white/[0.06]';
          const label =
            a.status === 'present'
              ? 'Present'
              : a.status === 'unavailable'
                ? 'Unavailable'
                : 'No response';
          return (
            <div
              key={a._id}
              className="flex items-center justify-between gap-2 rounded-lg border border-nurev-border bg-nurev-surface-lift px-3 py-2"
            >
              <div>
                <div className="font-medium text-nurev-text">{shObj.name}</div>
                <div className="text-[10px] text-nurev-muted">
                  {shObj.classAShares} shares
                </div>
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${labelCls}`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
//  Voting items section
// ─────────────────────────────────────────────────────────

function VotingItemsSection({
  meetingId,
  meeting,
  items,
  votes,
  addingItem,
  setAddingItem
}: {
  meetingId: string;
  meeting: BoardMeeting;
  items: VotingItem[];
  votes: Vote[];
  addingItem: boolean;
  setAddingItem: (v: boolean) => void;
}) {
  const addItem = useAddVotingItem(meetingId);
  const isInProgress = meeting.status === 'in_progress';
  const isScheduled = meeting.status === 'scheduled';

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionLabel>Voting items ({items.length})</SectionLabel>
        {(isScheduled || isInProgress) && !addingItem && (
          <button
            type="button"
            onClick={() => setAddingItem(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-nurev-border bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-nurev-text hover:border-amber-500/40"
          >
            <Plus className="h-3 w-3" /> Add item
          </button>
        )}
      </div>

      {addingItem && (
        <AddItemForm
          pending={addItem.isPending}
          onSubmit={async payload => {
            await addItem.mutateAsync(payload);
            setAddingItem(false);
          }}
          onCancel={() => setAddingItem(false)}
        />
      )}

      {items.length === 0 && !addingItem && (
        <div className="py-6 text-center text-sm text-nurev-muted">
          No voting items yet.
        </div>
      )}

      <div className="space-y-2">
        {items.map(item => (
          <VotingItemCard
            key={item._id}
            meetingId={meetingId}
            meeting={meeting}
            item={item}
            votes={votes.filter(
              v => String(v.votingItemId) === String(item._id)
            )}
          />
        ))}
      </div>
    </Card>
  );
}

function AddItemForm({
  onSubmit,
  onCancel,
  pending
}: {
  onSubmit: (payload: {
    title: string;
    description: string;
    itemType: VotingItemType;
  }) => void;
  onCancel: () => void;
  pending: boolean;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState<VotingItemType>('binary');
  const types: Array<{ value: VotingItemType; label: string }> = [
    { value: 'binary', label: 'Yes/No vote' },
    { value: 'information', label: 'Information only' }
  ];
  return (
    <div className="space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/[0.06] p-4">
      <input
        type="text"
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Item title (e.g. Approve Q1 budget)"
        className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text outline-none focus:border-amber-500/50"
      />
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        rows={2}
        placeholder="Description (optional)"
        className="w-full resize-none rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text outline-none focus:border-amber-500/50"
      />
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-nurev-muted">Type:</label>
        {types.map(t => {
          const selected = itemType === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setItemType(t.value)}
              className={`rounded-md border px-2.5 py-1 text-[11px] ${
                selected
                  ? 'border-amber-500/65 bg-amber-500/10 text-amber-300'
                  : 'border-nurev-border text-nurev-muted'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-nurev-border bg-white/[0.05] px-3 py-1.5 text-xs text-nurev-text"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSubmit({ title, description, itemType })}
          disabled={!title.trim() || pending}
          className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-nurev-bg transition hover:bg-amber-400 disabled:opacity-50"
        >
          Add item
        </button>
      </div>
    </div>
  );
}

function VotingItemCard({
  meetingId,
  meeting,
  item,
  votes
}: {
  meetingId: string;
  meeting: BoardMeeting;
  item: VotingItem;
  votes: Vote[];
}) {
  const castVote = useCastVote(meetingId, item._id);
  const declareConflict = useDeclareConflict(meetingId, item._id);
  const isInProgress = meeting.status === 'in_progress';

  const yes = votes.filter(v => v.choice === 'yes');
  const no = votes.filter(v => v.choice === 'no');
  const abstain = votes.filter(v => v.choice === 'abstain');
  const sharesFor = yes.reduce((s, v) => s + v.votedSharesAt, 0);
  const sharesAgainst = no.reduce((s, v) => s + v.votedSharesAt, 0);

  const outcomeCls =
    item.outcome === 'passed'
      ? 'text-emerald-300 bg-emerald-500/15'
      : item.outcome === 'failed'
        ? 'text-red-300 bg-red-500/15'
        : item.outcome === 'no_quorum'
          ? 'text-amber-300 bg-amber-500/15'
          : 'text-nurev-muted bg-white/[0.06]';
  const outcomeLabel =
    item.outcome === 'passed'
      ? 'PASSED'
      : item.outcome === 'failed'
        ? 'FAILED'
        : item.outcome === 'no_quorum'
          ? 'NO QUORUM'
          : 'PENDING';

  return (
    <div className="space-y-3 rounded-lg border border-nurev-border bg-nurev-surface-lift p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-nurev-text">
            {item.title}
          </div>
          {item.description && (
            <div className="mt-1 text-[12px] text-nurev-muted">
              {item.description}
            </div>
          )}
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${outcomeCls}`}
        >
          {outcomeLabel}
        </span>
      </div>

      {item.itemType === 'binary' && isInProgress && (
        <div className="flex flex-wrap items-center gap-2 border-t border-nurev-border pt-2">
          <span className="text-[11px] text-nurev-muted">Your vote:</span>
          <button
            type="button"
            onClick={() => castVote.mutateAsync({ choice: 'yes' })}
            disabled={castVote.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/35 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 disabled:opacity-50"
          >
            <Check className="h-2.5 w-2.5" /> Yes
          </button>
          <button
            type="button"
            onClick={() => castVote.mutateAsync({ choice: 'no' })}
            disabled={castVote.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-red-500/35 bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold text-red-300 disabled:opacity-50"
          >
            <X className="h-2.5 w-2.5" /> No
          </button>
          <button
            type="button"
            onClick={() => castVote.mutateAsync({ choice: 'abstain' })}
            disabled={castVote.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-white/20 bg-white/[0.06] px-2.5 py-1 text-[11px] font-semibold text-nurev-muted disabled:opacity-50"
          >
            <AlertCircle className="h-2.5 w-2.5" /> Abstain
          </button>
          <button
            type="button"
            onClick={() => declareConflict.mutateAsync()}
            disabled={declareConflict.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-amber-500/35 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold text-amber-300 disabled:opacity-50"
          >
            Declare conflict
          </button>
        </div>
      )}

      {item.itemType === 'binary' && (
        <div className="grid grid-cols-3 gap-2 border-t border-nurev-border pt-2 text-center">
          <div>
            <div className="text-sm font-bold text-emerald-300">{sharesFor}</div>
            <div className="text-[10px] uppercase tracking-wider text-nurev-muted">
              For
            </div>
          </div>
          <div>
            <div className="text-sm font-bold text-red-300">{sharesAgainst}</div>
            <div className="text-[10px] uppercase tracking-wider text-nurev-muted">
              Against
            </div>
          </div>
          <div>
            <div className="text-sm font-bold text-nurev-muted">
              {abstain.length}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-nurev-muted">
              Abstain
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
